import {
  Animation,
  EventTracker,
  Palette,
  ValueScale,
} from '@devexpress/dx-react-chart';
import {
  ArgumentAxis,
  BarSeries,
  Chart,
  Legend,
  Tooltip as DxTooltip,
  ValueAxis,
} from '@devexpress/dx-react-chart-material-ui';
import { Tooltip } from '@material-ui/core';
import Paper from '@material-ui/core/Paper';
import Info from '@material-ui/icons/InfoOutlined';
import { TooltipOverlay } from 'components';
import React from 'react';
import useStyles from './style';
import { IChartItemsAmountStockProps } from './types';

const ChartItemsAmountStock = ({
  data,
  config,
}: IChartItemsAmountStockProps) => {
  const classes = useStyles();

  const AgumentAxisLabelComponent = React.useCallback(
    (props: ArgumentAxis.LabelProps) => {
      return (
        <Tooltip title={props.text}>
          <ArgumentAxis.Label className={classes.labelItem} {...props} />
        </Tooltip>
      );
    },
    [classes.labelItem],
  );

  const ValueAxisLabelComponent = React.useCallback(
    ({ text, ...restProps }: ValueAxis.LabelProps) => {
      return <ValueAxis.Label text={`${text}%`} {...restProps} />;
    },
    [],
  );

  const LabelMarker = React.useCallback(
    ({ color }) => {
      return (
        <>
          <rect
            style={{
              width: 8,
              height: 8,
              background: color,
            }}
          />
          <Legend.Marker className={classes.labelMarker} color={color} />
        </>
      );
    },
    [classes.labelMarker],
  );

  const LabelComponent = React.useCallback(
    ({ ...props }) => {
      return <Legend.Label {...props} className={classes.labelText} />;
    },
    [classes.labelText],
  );

  const LabelRoot = React.useCallback(
    ({ children, ...rest }: Legend.RootProps) => {
      const reversecChildren = Array.isArray(children)
        ? [...children].reverse()
        : children;

      return (
        <Legend.Root {...rest} className={classes.labelContainer}>
          {reversecChildren}
        </Legend.Root>
      );
    },
    [classes.labelContainer],
  );

  const TooltipContent = React.useCallback(
    ({ targetItem, ...props }) => {
      const series = data[targetItem.point];

      const { percentCurrent, current, min, percentMin } = series;
      const textValue = `Qntd. mínima: ${min}; ${percentMin}%\nQntd. atual: ${current}; ${percentCurrent}% `;

      return (
        <DxTooltip.Content
          {...props}
          targetItem={targetItem}
          text={textValue}
          style={{ whiteSpace: 'pre-line' }}
        />
      );
    },
    [data],
  );

  const PointComponent = React.useCallback((props: BarSeries.PointProps) => {
    const { color } = props;

    return (
      <BarSeries.Point
        {...props}
        style={{
          fill: 'transparent',
          strokeWidth: 4,
          stroke: color,
        }}
      />
    );
  }, []);

  if (!data || !data.length) {
    return (
      <div className={classes.noData}>
        <Info />
        <p>Sem dados disponíveis</p>
      </div>
    );
  }

  return (
    <Paper className={classes.chartContainer}>
      <Chart data={data} height={250}>
        <Palette scheme={config.colorScheme} />
        <ValueScale
          name="percent"
          modifyDomain={() => [
            Math.min(...config.domain),
            Math.max(...config.domain),
          ]}
        />
        <ArgumentAxis labelComponent={AgumentAxisLabelComponent} />
        <ValueAxis
          scaleName="percent"
          labelComponent={ValueAxisLabelComponent}
        />

        <BarSeries
          scaleName="percent"
          name="Qntd. atual"
          valueField="percentCurrent"
          argumentField="label"
        />
        <BarSeries
          scaleName="percent"
          name="Qntd mínima"
          valueField="percentMin"
          argumentField="label"
          pointComponent={PointComponent}
        />

        <Animation />
        <Legend
          position="bottom"
          markerComponent={LabelMarker}
          labelComponent={LabelComponent}
          rootComponent={LabelRoot}
        />

        <EventTracker />

        <DxTooltip
          overlayComponent={TooltipOverlay}
          contentComponent={TooltipContent}
        />
      </Chart>
    </Paper>
  );
};

export default ChartItemsAmountStock;
